import React, {
  memo, useMemo, useState, useRef, useEffect, useCallback
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment, { isMoment } from 'moment';
import DatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';

import { t } from '@jotforminc/translation';
import { useSetAttributeToElement, useEventListener, usePrevious } from '@jotforminc/hooks';
import '../styles/_jfTimeRange.scss';
import '@jotforminc/jotform.css';
import {
  defaultFormat,
  defaultStorageFormat,
  startFormat,
  endFormat,
  startAllTime,
  lessThanStr,
  greaterThanStr,
  customStr,
  allTimeStr,
  timeOptionList,
  oldLessThanStr,
  oldGreaterThanStr,
  DAY_ABBREVIATIONS
} from './constants';
import { getDateThresholdRange, getTrueDateTimeStr } from '../utils';

const ContextWrapper = ({ usePortal, children }) => {
  return !usePortal ? children : createPortal(children, document.body);
};

ContextWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

const FilterTimeRange = ({
  isDisabled = false,
  timeFilterText = allTimeStr,
  filters,
  onRangeChange = () => { },
  showPredefinedTimeOptions = false,
  customTimeOptionList = timeOptionList,
  closeOnSelect = false,
  customHeader = false,
  usePortal = false,
  maxDate = false,
  minDate = false
}) => {
  const [timeFilterType, setTimeFilterType] = useState(timeFilterText);
  const [timeRange, setTimeRange] = useState({
    startDate: (filters[oldGreaterThanStr] && moment(filters[oldGreaterThanStr])) || startAllTime,
    endDate: (filters[oldLessThanStr] && moment(filters[oldLessThanStr])) || moment(),
    selectedDate: '',
    showDatePicker: 'start'
  });
  const {
    startDate, selectedDate, endDate, showDatePicker
  } = timeRange;
  const [isShowContextMenu, setIsShowContextMenu] = useState(false);
  const [isClickedTimeOptions, setIsClickedTimeOptions] = useState(false);
  const wrapperRef = useRef();
  const prevTimeFilterText = usePrevious(timeFilterText);
  const prevFilters = usePrevious(filters);
  const [popupStyle, setPopupStyle] = useState({});

  const dateRange = useCallback(() => {
    if (timeFilterText === allTimeStr) {
      return {
        startDate: startAllTime,
        endDate: moment(),
        showDatePicker: 'start'
      };
    }

    const startDateStr = getTrueDateTimeStr(filters[greaterThanStr] || startAllTime.format(defaultStorageFormat));
    const endDateStr = getTrueDateTimeStr(filters[lessThanStr] || moment().format(defaultStorageFormat));

    return {
      startDate: moment(startDateStr, startDateStr.includes(':') ? defaultStorageFormat : 'YYYY-MM-DD', true),
      endDate: moment(endDateStr, endDateStr.includes(':') ? defaultStorageFormat : 'YYYY-MM-DD', true)
    };
  }, [
    timeFilterText,
    filters
  ]);

  const handleRangeChange = useCallback(() => {
    const newFilter = { ...filters };

    if (timeFilterType === allTimeStr) {
      delete newFilter[lessThanStr];
      delete newFilter[greaterThanStr];
    } else {
      Object.keys(newFilter).filter(key => key !== lessThanStr || key !== greaterThanStr)
        .forEach(k => delete newFilter[k]); // all created_at related keys should be removed
      newFilter[lessThanStr] = endDate.format(endFormat);
      newFilter[greaterThanStr] = startDate.format(startFormat);
    }

    onRangeChange(timeFilterType, newFilter);
  }, [timeFilterType, startDate, endDate]);

  const handleClickOutside = e => {
    if (wrapperRef && !wrapperRef.current.contains(e.target)) {
      e.stopPropagation();
      handleRangeChange(timeRange);
      setIsShowContextMenu(false);
    }
  };

  const handleCollapseOnResize = () => {
    if (wrapperRef && wrapperRef.current) {
      handleRangeChange(timeRange);
      setIsShowContextMenu(false);
    }
  };

  useEventListener('click', handleClickOutside, { condition: isShowContextMenu, element: document.body, capture: true });

  useEventListener('resize', handleCollapseOnResize, { condition: usePortal, element: window, capture: true });

  useEffect(() => {
    setTimeFilterType(timeFilterText);
  }, [timeFilterText]);

  useEffect(() => {
    if ((prevTimeFilterText && (prevTimeFilterText !== timeFilterText))
        || (filters[greaterThanStr] !== (prevFilters && prevFilters[greaterThanStr]))
        || (filters[lessThanStr] !== (prevFilters && prevFilters[lessThanStr]))
    ) {
      setTimeRange({ ...timeRange, ...dateRange() });
    }
  }, [
    prevTimeFilterText,
    timeFilterText,
    prevFilters,
    filters,
    timeRange
  ]);

  useEffect(() => {
    if (closeOnSelect && isClickedTimeOptions) {
      handleRangeChange(timeRange);
      setIsShowContextMenu(false);
      setIsClickedTimeOptions(false);
    }
  }, [timeFilterType]);

  const customButtonText = useMemo(() => {
    if (timeFilterType === customStr && startDate.isValid() && endDate.isValid()) {
      return `${startDate.format(defaultFormat)} — ${endDate.format(defaultFormat)}`;
    }
    return timeFilterType === allTimeStr ? t(allTimeStr) : timeFilterType;
  }, [timeFilterType, startDate, endDate]);

  const handleChangeStart = _startDate => {
    const newStartDate = moment(_startDate);
    if (newStartDate.isAfter(timeRange.endDate)) {
      setTimeRange({
        ...timeRange,
        endDate: newStartDate,
        selectedDate: newStartDate
      });
    } else {
      setTimeRange({
        ...timeRange,
        startDate: newStartDate,
        selectedDate: timeRange.endDate,
        showDatePicker: 'end'
      });
    }
    setTimeFilterType(customStr);
  };

  const handleChangeEnd = _endDate => {
    const newEndDate = moment(_endDate);
    if (newEndDate.isBefore(timeRange.startDate)) {
      setTimeRange({
        ...timeRange,
        startDate: newEndDate,
        selectedDate: newEndDate
      });
    } else {
      setTimeRange({
        ...timeRange,
        endDate: newEndDate,
        showDatePicker: 'start',
        selectedDate: timeRange.startDate
      });
    }
    setTimeFilterType(customStr);
  };
  const handleTimeRangeOptionClick = _timeFilterType => () => {
    if (_timeFilterType !== timeFilterType) {
      const newDateTresholdRange = getDateThresholdRange(_timeFilterType);
      setTimeRange({ ...timeRange, ...newDateTresholdRange });
      setTimeFilterType(_timeFilterType);
      setIsClickedTimeOptions(true);
    }
  };

  const handleOpenMenu = ({ target }) => {
    if (!isDisabled) {
      if (usePortal) {
        const rect = target.getBoundingClientRect();
        setPopupStyle({
          position: 'absolute',
          left: rect.left,
          top: rect.top + 50,
          right: 'unset',
          width: rect.width,
          zIndex: 10000
        });
      }
      setIsShowContextMenu(true);
    }
  };

  useSetAttributeToElement('react-datepicker__week', 'tabindex', 0, isShowContextMenu);

  const _selectedDate = isMoment(selectedDate) ? selectedDate.toDate() : null;
  const _start = isMoment(startDate) ? startDate.toDate() : null;
  const _end = isMoment(endDate) ? endDate.toDate() : null;

  // To fix the datepickers month header not translating issue
  const CustomDatePickerHeader = ({
    // eslint-disable-next-line react/prop-types
    date, decreaseMonth, increaseMonth
  }) => {
    const formattedMonth = moment(date).format('MMMM');
    const formattedYear = moment(date).format('YYYY');
    return (
      <div style={{ fontSize: '14px', fontFamily: 'Circular, sans-serif' }}>
        <button
          type="button"
          aria-label="Previous Month"
          className="react-datepicker__navigation react-datepicker__navigation--previous"
          onClick={decreaseMonth}
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
            {'<'}
          </span>
        </button>
        <span className="react-datepicker__current-month">
          {t(formattedMonth)}
          &nbsp;
          {formattedYear}
        </span>
        <button
          type="button"
          aria-label="Next Month"
          className="react-datepicker__navigation react-datepicker__navigation--next"
          onClick={increaseMonth}
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
            {'>'}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="jSheetTimeRange-container" data-testid="filter-time-range">
      <div
        className={classNames(
          'searchFilter-select h-10 border border-navy-100 bg-white radius sm:ml-2.5 forTimeFilter flex items-center w-full pl-10 pr-8 justify-start sm:w-64',
          { isDisabled },
          { isOpened: isShowContextMenu }
        )}
        onClick={handleOpenMenu}
        onKeyDown={() => { }}
      >
        {/* <IconSVG name="date" className="jSheetSVG-date" /> */}
        <span className="jSheetButton-text">{t(customButtonText)}</span>
      </div>
      {isShowContextMenu && (
      <ContextWrapper usePortal={usePortal}>
        <div
          ref={wrapperRef}
          className="jSheetTimeRange jSheetContextMenu"
          style={popupStyle}
        >
          <div className={`jSheetDatePicker-container${showDatePicker === 'start' ? ' showStartDate' : ' showEndDate'}`}>
            <DatePicker
              inline
              calendarClassName="jSheetDatePicker-startDate"
              selected={_selectedDate}
              selectsStart
              startDate={_start}
              endDate={_end}
              onChange={handleChangeStart}
              disabledKeyboardNavigation
              renderCustomHeader={
                customHeader
                  ? ({ date, decreaseMonth, increaseMonth }) => (
                    <CustomDatePickerHeader
                      date={date}
                      decreaseMonth={decreaseMonth}
                      increaseMonth={increaseMonth}
                    />
                  )
                  : undefined
              }
              formatWeekDay={nameOfDay => t(DAY_ABBREVIATIONS[nameOfDay])}
              {...maxDate ? { maxDate } : {}}
              {...minDate ? { minDate } : {}}
            />
            <DatePicker
              inline
              calendarClassName="jSheetDatePicker-endDate"
              selected={_selectedDate}
              selectsEnd
              startDate={_start}
              endDate={_end}
              onChange={handleChangeEnd}
              disabledKeyboardNavigation
              renderCustomHeader={
                customHeader
                  ? ({ date, decreaseMonth, increaseMonth }) => (
                    <CustomDatePickerHeader
                      date={date}
                      decreaseMonth={decreaseMonth}
                      increaseMonth={increaseMonth}
                    />
                  )
                  : undefined
              }
              formatWeekDay={nameOfDay => t(DAY_ABBREVIATIONS[nameOfDay])}
              {...maxDate ? { maxDate } : {}}
              {...minDate ? { minDate } : {}}
            />
          </div>
          {showPredefinedTimeOptions && (
            <div className="jSheetDatePicker-buttons">
              <ul className="jSheetDateFormat forTimeRange">
                {customTimeOptionList.map(opt => (
                  <button
                    className="contextMenu-item"
                    onClick={handleTimeRangeOptionClick(opt)}
                    key={opt}
                    type="button"
                  >
                    <span
                      className={`contextMenu-itemLabel${timeFilterType === opt ? ' active' : ''}`}
                    >
                      {t(opt)}
                    </span>
                  </button>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ContextWrapper>
      )}
    </div>
  );
};

FilterTimeRange.propTypes = {
  filters: PropTypes.shape({}).isRequired,
  isDisabled: PropTypes.bool,
  onRangeChange: PropTypes.func,
  timeFilterText: PropTypes.string,
  showPredefinedTimeOptions: PropTypes.bool,
  customTimeOptionList: PropTypes.array,
  closeOnSelect: PropTypes.bool,
  customHeader: PropTypes.bool,
  usePortal: PropTypes.bool,
  maxDate: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Date)
  ]),
  minDate: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Date)
  ])
};

export default memo(FilterTimeRange);
